import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  IconButton,
  Avatar,
  List,
  ListItemText,
  ListItemAvatar,
  ListItemButton,
  Divider,
  TextField,
  InputAdornment,
  Paper,
  Button,
  Tabs,
  Tab,
  Chip,
  Badge,
  Menu,
  MenuItem,
  CircularProgress,
  Fade,
  Alert,
  Snackbar,
  Select,
  InputLabel,
  FormControl,
  Tooltip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Send as SendIcon,
  Campaign as CampaignIcon,
  Phone as PhoneIcon,
  MoreVert as MoreVertIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  ChatBubble as ChatIcon,
  Delete as DeleteIcon,
  Block as BlockIcon,
  Notifications as NotificationsIcon,
  AttachFile as AttachFileIcon,
  Mic as MicIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Assignment as AssignmentIcon,
  Archive as ArchiveIcon,
  ExitToApp as LogoutIcon,
} from "@mui/icons-material";
import {
  useGetChatListQuery,
  useSendMessageMutation,
  useAssignchatMutation,
  useGetchatHistoryQuery,
  useGetWhatsappChatAgentsQuery,
  useSendTypingStatusMutation,
  useGetTypingStatusQuery,
  useMarkMessageReadMutation,
  useAgentstatusMutation,
  useChatTransferMutation, // ADDED: Import the transfer mutation
} from "../store/services/chatapi";
import { logout } from "../store/slices/authSlice";
import { useDispatch } from "react-redux";

// --- Styled Components ---

const DashboardContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  height: "100vh",
  backgroundColor: "#F1F5F9",
  padding: theme.spacing(2),
  gap: theme.spacing(2),
  overflow: "hidden",
}));

const StyledSidebar = styled(Paper)(({ theme }) => ({
  width: 380,
  display: "flex",
  flexDirection: "column",
  borderRadius: "16px",
  backgroundColor: "#FFFFFF",
  border: "1px solid #E2E8F0",
  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.05)",
  overflow: "hidden",
}));

const StyledChatArea = styled(Paper)(({ theme }) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  borderRadius: "16px",
  backgroundColor: "#FFFFFF",
  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.05)",
  overflow: "hidden",
  border: "1px solid #E2E8F0",
  position: "relative",
}));

const MessageBubble = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isUser",
})(({ theme, isUser }) => ({
  padding: theme.spacing(1.5, 2.5),
  borderRadius: isUser ? "20px 20px 4px 20px" : "20px 20px 20px 4px",
  maxWidth: "75%",
  position: "relative",
  wordBreak: "break-word",
  boxShadow: isUser
    ? "0 4px 15px rgba(33, 150, 243, 0.2)"
    : "0 1px 3px rgba(0,0,0,0.05)",
  background: isUser
    ? "linear-gradient(135deg, #29B6F6 0%, #0288D1 100%)"
    : "#FFFFFF",
  color: isUser ? "#ffffff" : "#37474f",
  marginBottom: theme.spacing(1.5),
  fontSize: "0.95rem",
  lineHeight: 1.5,
}));

// Typing indicator animation
const TypingIndicator = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: 4,
  '& .dot': {
    width: 6,
    height: 6,
    borderRadius: '50%',
    backgroundColor: '#0284C7',
    animation: 'typing 1.5s infinite ease-in-out',
    '&:nth-child(2)': {
      animationDelay: '0.2s',
    },
    '&:nth-child(3)': {
      animationDelay: '0.4s',
    },
  },
  '@keyframes typing': {
    '0%, 100%': {
      transform: 'translateY(0)',
      opacity: 0.4,
    },
    '50%': {
      transform: 'translateY(-4px)',
      opacity: 1,
    },
  },
}));

// --- Helper Functions ---

const formatTime = (timestamp) => {
  const date = new Date(timestamp);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const formatLastSeen = (timestamp) => {
  if (!timestamp) return "Unknown";
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffHours < 24) return `${diffHours} hours ago`;
  return `${diffDays} days ago`;
};

const handleApiError = (error) => {
  console.error("API Error:", error);
  if (error?.status === 401) return "Unauthorized. Please check your credentials.";
  if (error?.status === 403) return "You don't have permission to send messages.";
  if (error?.status === 404) return "Contact not found.";
  if (error?.data?.message) return error.data.message;
  if (error?.error) return error.error;
  return "Failed to send message. Please try again.";
};

export default function LiveChatDashboard() {
  const location = useLocation();
  const customerId = location.state?.customerId;
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const isTypingRef = useRef(false);

  // --- State ---
  const [selectedContact, setSelectedContact] = useState(null);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [tabValue, setTabValue] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [contactStatuses, setContactStatuses] = useState({});
  const [readMessages, setReadMessages] = useState(new Set());
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [localMessages, setLocalMessages] = useState({}); // Only for optimistic messages
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [contacts, setContacts] = useState({});
  // Track total unread count
  const [totalUnread, setTotalUnread] = useState(0);
  const [filterDays, setFilterDays] = useState(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  // --- API Hooks ---
  const { data: apiData, isLoading, refetch, error: apiError } = useGetChatListQuery();
  const [sendMessageApi, { isLoading: isSending }] = useSendMessageMutation();
  const [assignChatApi, { isLoading: isAssigning }] = useAssignchatMutation();
  const [markMessageRead] = useMarkMessageReadMutation();
  const [updateAgentStatus] = useAgentstatusMutation();
  // ADDED: Chat transfer mutation
  const [chatTransfer, { isLoading: isTransferring }] = useChatTransferMutation();

  const {
    data: agentsResponse,
    isLoading: isLoadingAgents,
  } = useGetWhatsappChatAgentsQuery(customerId, { skip: !customerId });

  const {
    data: chatHistoryData,
    isLoading: isLoadingHistory,
    refetch: refetchHistory
  } = useGetchatHistoryQuery(
    selectedContact?.phone || null,
    { skip: !selectedContact?.phone }
  );

  // ============================================================================
  // 🔥 FIX PART 1 — STOP POLLING TYPING STATUS
  // ============================================================================
  const {
    data: typingData,
    refetch: refetchTyping
  } = useGetTypingStatusQuery(selectedContact?.phone, {
    skip: !selectedContact?.phone,
    refetchOnMountOrArgChange: false,
    pollingInterval: 0
  });

  const [sendTypingStatus] = useSendTypingStatusMutation();

  // ============================================================================
  // 🔥 FIX PART 4 — STABLE TYPING UI (NO FLICKER)
  // ============================================================================
  const isOtherAgentTyping = Boolean(
    typingData?.typing_agents && typingData.typing_agents.length > 0
  );

  // --- Effects ---

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // ============================================================================
  // ✅ OPTIONAL: Refresh typing status every 5s ONLY when chat open
  // ============================================================================
  useEffect(() => {
    if (!selectedContact) return;

    const interval = setInterval(() => {
      refetchTyping();
    }, 5000); // Very slow, no flicker

    return () => clearInterval(interval);
  }, [selectedContact, refetchTyping]);

  // ============================================================================
  // 🔥 FIX PART 3 — RESET TYPING ON CHAT CHANGE
  // ============================================================================
  useEffect(() => {
    isTypingRef.current = false;

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
  }, [selectedContact]);

  // Mark messages as read when chat opens
  useEffect(() => {
    if (!chatHistoryData?.messages || !selectedContact) return;

    const unreadMessages = chatHistoryData.messages.filter(
      msg => !msg.is_read && msg.direction === "inbound"
    );

    unreadMessages.forEach(msg => {
      markMessageRead(msg.message_id);
    });
  }, [chatHistoryData, selectedContact, markMessageRead]);

  useEffect(() => {
    if (apiData && !isLoading) {
      const contactsByPhone = {};
      const allMessages = [...(apiData.unassigned || []), ...(apiData.assigned || [])];

      allMessages.forEach((message) => {
        const phone = message.sender_mobile_number;
        if (!contactsByPhone[phone]) {
          contactsByPhone[phone] = {
            id: phone,
            name: `User ${phone.slice(-4)}`,
            phone: phone,
            rawPhone: phone,
            unread: !message.is_read ? 1 : 0,
            status: "online",
            lastSeen: message.timestamp,
            lastMessage: message.message_content,
            lastMessageTime: message.timestamp,
            assigned: apiData.assigned?.some(m => m.sender_mobile_number === phone) || false
          };
        } else {
          contactsByPhone[phone].unread += !message.is_read ? 1 : 0;
          if (new Date(message.timestamp) > new Date(contactsByPhone[phone].lastMessageTime)) {
            contactsByPhone[phone].lastSeen = message.timestamp;
            contactsByPhone[phone].lastMessage = message.message_content;
            contactsByPhone[phone].lastMessageTime = message.timestamp;
          }
          if (apiData.assigned?.some(m => m.sender_mobile_number === phone)) {
            contactsByPhone[phone].assigned = true;
          }
        }
      });
      setContacts(contactsByPhone);
      const total = Object.values(contactsByPhone).reduce((sum, contact) => sum + contact.unread, 0);
      setTotalUnread(total);
    }
  }, [apiData, isLoading]);

  // Message merge effect
  useEffect(() => {
    if (!selectedContact) return;

    let combinedMessages = [];

    // backend messages (single source of truth)
    if (chatHistoryData?.messages) {
      combinedMessages = chatHistoryData.messages.map(msg => ({
        id: msg.message_id,
        sender: msg.direction === "outbound" ? "agent" : "user",
        text: msg.content,
        time: formatTime(msg.timestamp),
        timestamp: msg.timestamp,
        read: msg.is_read,
        isOptimistic: false,
        status: msg.status
      }));
    }

    // optimistic messages (only while sending)
    const optimistic = localMessages[selectedContact.phone] || [];

    const merged = [...combinedMessages, ...optimistic]
      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

    setMessages(merged);

    // If there is a new inbound message that's newer than our local resolved state, 
    // we should re-enable the input box.
    if (merged.length > 0) {
      const lastMsg = merged[merged.length - 1];
      if (lastMsg.sender === 'user' && contactStatuses[selectedContact.id] === 'resolved') {
        setContactStatuses(prev => {
          const updated = { ...prev };
          delete updated[selectedContact.id];
          return updated;
        });
      }
    }
  }, [selectedContact, chatHistoryData, localMessages, contactStatuses]);

  // Cleanup typing timeout on unmount
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);

  // --- Handlers ---

  const campaigns = useMemo(() => {
    if (Object.keys(contacts).length === 0) return [];
    const contactsArray = Object.values(contacts);
    const sortedContacts = contactsArray.sort(
      (a, b) => new Date(b.lastMessageTime) - new Date(a.lastMessageTime)
    );
    return [
      { id: 1, name: "Assigned", contacts: sortedContacts.filter(c => c.assigned), color: "#4caf50" },
      { id: 2, name: "Unassigned", contacts: sortedContacts.filter(c => !c.assigned), color: "#29B6F6" }
    ];
  }, [contacts]);

  const handleAssignChat = useCallback(async (contact) => {
    if (!contact) return;
    try {
      const payload = { sender_mobile_number: contact.phone };
      await assignChatApi(payload).unwrap();

      setContacts(prev => ({
        ...prev,
        [contact.id]: { ...prev[contact.id], assigned: true }
      }));
      if (selectedContact?.id === contact.id) {
        setSelectedContact(prev => ({ ...prev, assigned: true }));
      }
      setSnackbar({ open: true, message: "Chat assigned successfully", severity: "success" });
      setTimeout(() => {
        refetch();
        if (selectedContact?.phone === contact.phone) refetchHistory();
      }, 1000);
    } catch (error) {
      let errorMessage = "Failed to assign chat";
      if (error?.data?.message) errorMessage = error.data.message;
      else if (error?.error) errorMessage = error.error;
      else if (error?.status === 400) errorMessage = "Invalid request.";
      setSnackbar({ open: true, message: errorMessage, severity: "error" });
    }
  }, [assignChatApi, refetch, refetchHistory, selectedContact]);

  const handleContactSelect = useCallback(async (contact) => {
    setSelectedContact(contact);
    if (!contact.assigned) {
      await handleAssignChat(contact);
    }
    if (contact.unread > 0) {
      setContacts(prev => ({
        ...prev,
        [contact.id]: { ...prev[contact.id], unread: 0 }
      }));
      setTotalUnread(prev => Math.max(0, prev - contact.unread));
      if (chatHistoryData?.messages) {
        const messageIds = chatHistoryData.messages.map(msg => msg.message_id);
        setReadMessages(prev => {
          const newSet = new Set(prev);
          messageIds.forEach(id => newSet.add(id));
          return newSet;
        });
      }
    }
  }, [chatHistoryData, handleAssignChat]);

  const handleRefresh = useCallback(() => {
    refetch();
    if (selectedContact) refetchHistory();
    setSnackbar({ open: true, message: "Chat data refreshed", severity: "success" });
  }, [refetch, refetchHistory, selectedContact]);

  // ============================================================================
  // 🔥 FIX PART 2 — DEBOUNCE sendTypingStatus
  // ============================================================================
  const handleTyping = useCallback((value) => {
    setInput(value);

    if (!selectedContact) return;

    if (!isTypingRef.current) {
      isTypingRef.current = true;
      sendTypingStatus({
        phone: selectedContact.phone,
        is_typing: true
      }).catch(() => { });
    }

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    typingTimeoutRef.current = setTimeout(() => {
      sendTypingStatus({
        phone: selectedContact.phone,
        is_typing: false
      }).catch(() => { });
      isTypingRef.current = false;
    }, 1500);
  }, [selectedContact, sendTypingStatus]);

  const handleSendMessage = async () => {
    if (!input.trim() || !selectedContact) return;

    const tempId = `temp-${Date.now()}`;

    const optimisticMessage = {
      id: tempId,
      sender: "agent",
      text: input,
      time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
      timestamp: new Date().toISOString(),
      isOptimistic: true,
      read: false,
      status: "sending"
    };

    // Store ONLY in localMessages (not messages)
    setLocalMessages(prev => ({
      ...prev,
      [selectedContact.phone]: [
        ...(prev[selectedContact.phone] || []),
        optimisticMessage
      ]
    }));

    // Update sidebar preview
    setContacts(prev => ({
      ...prev,
      [selectedContact.id]: {
        ...prev[selectedContact.id],
        lastMessage: input,
        lastMessageTime: new Date().toISOString(),
        lastSeen: new Date().toISOString()
      }
    }));

    const messageText = input;
    setInput("");

    try {
      await sendMessageApi({
        sender_mobile_number: selectedContact.phone,
        message: messageText
      }).unwrap();

      // Remove optimistic message once backend confirms
      setLocalMessages(prev => ({
        ...prev,
        [selectedContact.phone]:
          (prev[selectedContact.phone] || []).filter(m => m.id !== tempId)
      }));

      refetchHistory(); // backend becomes single source

      setSnackbar({
        open: true,
        message: "Message sent successfully",
        severity: "success"
      });
    } catch (error) {
      // Remove optimistic message on failure
      setLocalMessages(prev => ({
        ...prev,
        [selectedContact.phone]:
          (prev[selectedContact.phone] || []).filter(m => m.id !== tempId)
      }));

      setSnackbar({
        open: true,
        message: handleApiError(error),
        severity: "error"
      });
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleMarkAllAsRead = () => {
    const updatedContacts = { ...contacts };
    Object.keys(updatedContacts).forEach(phone => { updatedContacts[phone].unread = 0; });
    setContacts(updatedContacts);
    setTotalUnread(0);
    if (chatHistoryData?.messages) {
      const allMessageIds = chatHistoryData.messages.map(msg => msg.message_id);
      setReadMessages(new Set(allMessageIds));
    }
    setSnackbar({ open: true, message: "All messages marked as read", severity: "success" });
  };

  const handleMarkContactAsRead = (contactId) => {
    if (contacts[contactId] && contacts[contactId].unread > 0) {
      setContacts(prev => ({
        ...prev,
        [contactId]: {
          ...prev[contactId],
          unread: 0
        }
      }));
      setTotalUnread(prev => Math.max(0, prev - contacts[contactId].unread));
      if (chatHistoryData?.messages) {
        const messageIds = chatHistoryData.messages.map(msg => msg.message_id);
        setReadMessages(prev => {
          const newSet = new Set(prev);
          messageIds.forEach(id => newSet.add(id));
          return newSet;
        });
      }
    }
  };

  // ============================================================================
  // ✅ FIX 1 — MOVE SIDEBAR useMemo OUTSIDE JSX
  // ============================================================================
  const renderedCampaignLists = useMemo(() => {
    return campaigns.map((camp) => {
      if (tabValue !== camp.id - 1) return null;

      return (
        <List key={camp.id} sx={{ p: 0, pt: 1 }}>
          {camp.contacts
            .filter(c => {
              const matchesSearch =
                c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                c.phone.includes(searchTerm);

              if (!matchesSearch) return false;

              if (filterDays !== null && c.lastMessageTime) {
                const msgDate = new Date(c.lastMessageTime);
                const cutoffDate = new Date();
                cutoffDate.setDate(cutoffDate.getDate() - filterDays);
                return msgDate >= cutoffDate;
              }
              return true;
            })
            .map((contact) => (
              <ListItemButton
                key={contact.id}
                selected={selectedContact?.id === contact.id}
                onClick={() => handleContactSelect(contact)}
                sx={{
                  borderRadius: "12px",
                  mb: 1,
                  py: 1.5,
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  border: "1px solid transparent",
                  '&.Mui-selected': {
                    bgcolor: "#F0F9FF",
                    borderLeft: "4px solid #0288D1",
                    borderColor: "transparent",
                    '&:hover': { bgcolor: "#E1F5FE" }
                  },
                  '&:hover': {
                    bgcolor: "#FFFFFF",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                    transform: "translateY(-2px)"
                  }
                }}
              >
                <ListItemAvatar>
                  <Badge
                    overlap="circular"
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    variant="dot"
                    sx={{
                      '& .MuiBadge-badge': {
                        bgcolor: contact.status === 'online' ? '#4caf50' : '#bdbdbd',
                        border: '2px solid white',
                        width: 10,
                        height: 10,
                        borderRadius: '50%'
                      }
                    }}
                  >
                    <Avatar
                      sx={{
                        background: selectedContact?.id === contact.id ? "linear-gradient(135deg, #29B6F6 0%, #0288D1 100%)" : "#F1F5F9",
                        color: selectedContact?.id === contact.id ? "#fff" : "#64748B",
                        fontWeight: "bold",
                        border: selectedContact?.id === contact.id ? "none" : "1px solid #E2E8F0"
                      }}
                    >
                      {contact.name[0]}
                    </Avatar>
                  </Badge>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Typography variant="body1" fontWeight={selectedContact?.id === contact.id ? "700" : "600"} color="text.primary" noWrap>
                        {contact.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                        {formatLastSeen(contact.lastSeen)}
                      </Typography>
                    </Box>
                  }
                  secondary={
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 0.5 }}>
                      <Typography variant="body2" color={contact.unread > 0 ? "text.primary" : "text.secondary"} noWrap sx={{ maxWidth: 160, fontWeight: contact.unread > 0 ? 600 : 400 }}>
                        {contact.lastMessage || "No messages"}
                      </Typography>
                      {contact.unread > 0 && (
                        <Chip
                          label={contact.unread}
                          size="small"
                          sx={{
                            height: 20,
                            minWidth: 20,
                            fontSize: '0.7rem',
                            fontWeight: 'bold',
                            bgcolor: "#FF4081",
                            color: "white"
                          }}
                        />
                      )}
                    </Box>
                  }
                />
              </ListItemButton>
            ))}
        </List>
      );
    });
  }, [campaigns, tabValue, searchTerm, filterDays, selectedContact, handleContactSelect]);

  // ============================================================================
  // ✅ FIX 2 — MOVE MESSAGE useMemo OUTSIDE JSX
  // ============================================================================
  const renderedMessages = useMemo(() => {
    return messages.map((message, index) => {
      const isNewDay =
        index === 0 ||
        new Date(message.timestamp).toDateString() !==
        new Date(messages[index - 1].timestamp).toDateString();

      let dateLabel = "";
      if (isNewDay) {
        const msgDate = new Date(message.timestamp);
        const today = new Date();
        const yesterday = new Date();
        yesterday.setDate(today.getDate() - 1);

        if (msgDate.toDateString() === today.toDateString()) dateLabel = "Today";
        else if (msgDate.toDateString() === yesterday.toDateString()) dateLabel = "Yesterday";
        else dateLabel = msgDate.toLocaleDateString([], { month: 'short', day: 'numeric' });
      }

      return (
        <React.Fragment key={message.id}>
          {isNewDay && (
            <Box sx={{ display: "flex", justifyContent: "center", my: 2, opacity: 0.8 }}>
              <Chip
                label={dateLabel}
                size="small"
                sx={{
                  bgcolor: "#E2E8F0",
                  color: "#64748B",
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  height: 24
                }}
              />
            </Box>
          )}

          <Box
            sx={{
              display: "flex",
              justifyContent: message.sender === "agent" ? "flex-end" : "flex-start",
              mb: 1
            }}
          >
            <MessageBubble isUser={message.sender === "agent"}>
              <Typography variant="body1" sx={{ fontSize: '0.95rem', letterSpacing: '0.01em' }}>
                {message.text}
                {message.isOptimistic && (
                  <Box component="span" sx={{ ml: 1, display: 'inline-flex' }}>
                    <CircularProgress size={12} color="inherit" />
                  </Box>
                )}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.5, mt: 0.5 }}>
                <Typography variant="caption" sx={{ fontSize: '0.65rem', opacity: 0.8 }}>
                  {message.time}
                </Typography>
                {message.sender === "agent" && !message.isOptimistic && (
                  <Typography variant="caption" sx={{ fontSize: '0.65rem', opacity: 0.9 }}>
                    {message.read ? '✓✓' : '✓'}
                  </Typography>
                )}
              </Box>
            </MessageBubble>
          </Box>
        </React.Fragment>
      );
    });
  }, [messages]);

  // --- Render ---

  // Set Online Status on Mount
  useEffect(() => {
    updateAgentStatus({ status: 'online' });

    // Optional: Set offline on window close
    const handleBeforeUnload = () => {
      // Note: This is not guaranteed to fire reliable network requests on close
      // simpler to rely on heartbeat or explicit logout
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [updateAgentStatus]);

  const handleLogout = async () => {
    try {
      await updateAgentStatus({ status: 'offline' }).unwrap();
    } catch (err) {
      console.error("Failed to set offline status", err);
    }
    dispatch(logout());
    navigate("/", { replace: true });
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", height: "100vh", justifyContent: "center", alignItems: "center", bgcolor: "#f5f7fa" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (apiError) {
    return (
      <Box sx={{ p: 4, display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
        <Typography color="error">Failed to load chats</Typography>
        <Button startIcon={<RefreshIcon />} variant="outlined" onClick={handleRefresh}>Retry</Button>
      </Box>
    );
  }

  return (
    <DashboardContainer>
      {/* Sidebar - Floating Glass Panel */}
      <StyledSidebar elevation={0}>
        {/* Sidebar Header */}
        <Box sx={{ p: 3, pb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Avatar sx={{ bgcolor: 'transparent', width: 32, height: 32 }}>
                <CampaignIcon sx={{ color: '#0277BD', fontSize: 32 }} />
              </Avatar>
              <Typography variant="h5" fontWeight="700" sx={{
                color: "#1E293B",
                letterSpacing: "-0.5px"
              }}>
                Live Chat
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Tooltip title="Refresh">
                <IconButton size="small" onClick={handleRefresh} sx={{ color: "#78909c", bgcolor: "#f5f5f5" }}>
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Logout">
                <IconButton size="small" onClick={handleLogout} sx={{ color: "#ef5350", bgcolor: "#ffebee" }}>
                  <LogoutIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          {/* Search */}
          <TextField
            fullWidth
            size="small"
            placeholder="Search conversations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#90a4ae" }} />
                </InputAdornment>
              ),
              sx: {
                bgcolor: "#F8FAFC",
                color: "#334155",
                borderRadius: "12px",
                border: "1px solid #E2E8F0",
                transition: "all 0.2s",
                '&:hover': { bgcolor: "#F1F5F9" },
                '&.Mui-focused': {
                  bgcolor: "#ffffff",
                  border: "1px solid #0284C7",
                  boxShadow: "0 0 0 3px rgba(2, 132, 199, 0.1)"
                },
                '& fieldset': { border: 'none' },
                pl: 2
              }
            }}
          />

          <FormControl fullWidth size="small" sx={{ mt: 2 }}>
            <Select
              value={filterDays === null ? 'all' : filterDays}
              onChange={(e) => setFilterDays(e.target.value === 'all' ? null : e.target.value)}
              displayEmpty
              renderValue={(selected) => (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: selected === 'all' ? "#64748B" : "#0284C7" }}>
                  <FilterIcon fontSize="small" color={selected === 'all' ? "action" : "primary"} />
                  <Typography variant="body2" fontWeight="600">
                    {selected === 'all' ? 'Filter by Date' : `Last ${selected} Days`}
                  </Typography>
                </Box>
              )}
              sx={{
                bgcolor: "#F8FAFC",
                borderRadius: "12px",
                border: "1px solid #E2E8F0",
                '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                '&:hover': { bgcolor: "#F1F5F9" },
                '& .MuiSelect-select': { py: 1.5 }
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    borderRadius: "12px",
                    mt: 1,
                    boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                    border: "1px solid #E2E8F0"
                  }
                }
              }}
            >
              <MenuItem value="all" sx={{ borderRadius: "8px", m: 0.5, fontWeight: 500 }}>All Time</MenuItem>
              <Divider sx={{ my: 0.1 }} />
              <MenuItem value={2} sx={{ borderRadius: "8px", m: 0.5, fontWeight: 500 }}>Last 2 Days</MenuItem>
              <MenuItem value={4} sx={{ borderRadius: "8px", m: 0.5, fontWeight: 500 }}>Last 4 Days</MenuItem>
              <MenuItem value={5} sx={{ borderRadius: "8px", m: 0.5, fontWeight: 500 }}>Last 5 Days</MenuItem>
              <MenuItem value={10} sx={{ borderRadius: "8px", m: 0.5, fontWeight: 500 }}>Last 10 Days</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Tabs */}
        <Tabs
          value={tabValue}
          onChange={(e, v) => setTabValue(v)}
          variant="fullWidth"
          sx={{
            borderBottom: "1px solid #F1F5F9",
            minHeight: 48,
            '& .MuiTabs-indicator': {
              height: 2,
              borderRadius: "3px 3px 0 0",
              bgcolor: "#0284C7"
            }
          }}
        >
          <Tab label={`Assigned (${campaigns[0]?.contacts?.length || 0})`} sx={{ textTransform: 'none', fontSize: '0.95rem', fontWeight: 600 }} />
          <Tab label={`Unassigned (${campaigns[1]?.contacts?.length || 0})`} sx={{ textTransform: 'none', fontSize: '0.95rem', fontWeight: 600 }} />
        </Tabs>

        {/* Contact List */}
        <Box sx={{ flex: 1, overflowY: "auto", px: 2 }}>
          {renderedCampaignLists}
        </Box>
      </StyledSidebar>

      {/* Main Chat Area - Floating Glass Panel */}
      <StyledChatArea elevation={0}>
        {selectedContact ? (
          <>
            {/* Chat Header */}
            <Paper elevation={0} sx={{
              p: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: "1px solid #f0f0f0",
              bgcolor: "transparent"
            }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Avatar sx={{
                  bgcolor: "#E0F2FE",
                  color: "#0284C7",
                  fontWeight: "bold",
                  border: "1px solid #BAE6FD"
                }}>{selectedContact.name[0]}</Avatar>
                <Box>
                  <Typography variant="h6" fontWeight="700" color="text.primary">{selectedContact.name}</Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: 'wrap' }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: selectedContact.assigned ? '#4caf50' : '#ff9800' }} />
                    <Typography variant="caption" color="text.secondary">
                      {selectedContact.phone} • {selectedContact.assigned ? "Assigned" : "Unassigned"}
                    </Typography>
                    {/* Typing Indicator */}
                    {isOtherAgentTyping && (
                      <Typography variant="caption" sx={{ color: "#0284C7", fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <TypingIndicator>
                          <Box className="dot" />
                          <Box className="dot" />
                          <Box className="dot" />
                        </TypingIndicator>
                        Typing...
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                {/* Agent Select - Only if assigned */}
                {selectedContact.assigned && agentsResponse && (
                  <FormControl size="small" sx={{ minWidth: 150 }} disabled={isTransferring}>
                    <InputLabel>Agent</InputLabel>
                    <Select
                      label="Agent"
                      value={selectedAgent?.agent_id || ""}
                      onChange={async (e) => {
                        const agent = agentsResponse.find(a => a.agent_id === e.target.value);

                        try {
                          await chatTransfer({
                            sender_mobile_number: selectedContact.phone,
                            target_agent_id: agent.agent_id,
                          }).unwrap();

                          setSelectedAgent(agent);

                          setSnackbar({
                            open: true,
                            message: `Chat transferred to ${agent.name}`,
                            severity: "success",
                          });

                          refetch(); // refresh sidebar
                          refetchHistory(); // refresh messages

                        } catch (error) {
                          setSnackbar({
                            open: true,
                            message: handleApiError(error),
                            severity: "error",
                          });
                        }
                      }}
                      renderValue={(selected) => {
                        const agent = agentsResponse.find(a => a.agent_id === selected);
                        return (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            {agent?.name || "Select Agent"}
                            {isTransferring && <CircularProgress size={16} />}
                          </Box>
                        );
                      }}
                    >
                      {agentsResponse.map(agent => (
                        <MenuItem key={agent.agent_id} value={agent.agent_id}>
                          {agent.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                <IconButton onClick={handleMenuOpen}><MoreVertIcon /></IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}
                >
                  <MenuItem onClick={() => {
                    setContactStatuses(prev => ({ ...prev, [selectedContact.id]: 'resolved' }));
                    setSnackbar({ open: true, message: "Marked as resolved", severity: "success" });
                    handleMenuClose();
                  }}>
                    <ArchiveIcon sx={{ mr: 1, fontSize: 20 }} /> Resolved
                  </MenuItem>
                  <MenuItem onClick={() => {
                    handleMarkContactAsRead(selectedContact.id);
                    handleMenuClose();
                  }}>
                    <CheckCircleIcon sx={{ mr: 1, fontSize: 20 }} /> Mark Read
                  </MenuItem>
                </Menu>
              </Box>
            </Paper>

            {/* Messages Area */}
            <Box sx={{
              flex: 1,
              p: 3,
              overflowY: "auto",
              bgcolor: "#fff",
            }}>
              {isLoadingHistory ? (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}><CircularProgress /></Box>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  {renderedMessages}

                  {/* Session Ended Indicator */}
                  {contactStatuses[selectedContact.id] === 'resolved' && (
                    <Box sx={{ display: "flex", justifyContent: "center", my: 4, opacity: 0.9 }}>
                      <Chip
                        label="Session Ended"
                        size="small"
                        icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                        sx={{
                          bgcolor: "#F1F5F9",
                          color: "#64748B",
                          fontSize: "0.75rem",
                          fontWeight: 700,
                          height: 28,
                          border: "1px solid #E2E8F0",
                          px: 1
                        }}
                      />
                    </Box>
                  )}

                  <div ref={messagesEndRef} />
                </Box>
              )}
            </Box>

            {/* Input Area - Floating Capsule */}
            {selectedContact.assigned ? (
              <Box sx={{ p: 2, bgcolor: "transparent" }}>
                <Paper
                  elevation={4}
                  sx={{
                    p: "6px 16px",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    borderRadius: "24px",
                    border: "none",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.08)"
                  }}
                >
                  <IconButton sx={{ color: "#94A3B8", p: 1 }}><AttachFileIcon /></IconButton>
                  <TextField
                    fullWidth
                    disabled={contactStatuses[selectedContact.id] === 'resolved'}
                    placeholder={contactStatuses[selectedContact.id] === 'resolved' ? "Session ended. Waiting for user reply..." : "Type your message..."}
                    size="small"
                    multiline
                    maxRows={3}
                    value={input}
                    onChange={(e) => handleTyping(e.target.value)}
                    onKeyPress={handleKeyPress}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: contactStatuses[selectedContact.id] === 'resolved' ? "#F8FAFC" : "transparent",
                        p: 0,
                        '& fieldset': { border: 'none' }
                      },
                      '& .MuiInputBase-input': {
                        color: "#334155",
                        fontSize: "0.95rem"
                      }
                    }}
                  />
                  {input.trim() ? (
                    <IconButton
                      onClick={handleSendMessage}
                      disabled={isSending || contactStatuses[selectedContact.id] === 'resolved'}
                      sx={{
                        background: "linear-gradient(135deg, #29B6F6 0%, #0288D1 100%)",
                        color: "white",
                        width: 44,
                        height: 44,
                        boxShadow: "0 4px 12px rgba(2, 136, 209, 0.3)",
                        '&:hover': {
                          background: "linear-gradient(135deg, #039BE5 0%, #01579B 100%)",
                          transform: "scale(1.05)"
                        },
                        transition: "all 0.2s"
                      }}
                    >
                      {isSending ? <CircularProgress size={20} color="inherit" /> : <SendIcon sx={{ ml: 0.5, fontSize: 20 }} />}
                    </IconButton>
                  ) : (
                    <IconButton sx={{ color: "#94A3B8" }}><MicIcon /></IconButton>
                  )}
                </Paper>
              </Box>
            ) : (
              <Box sx={{ p: 3, m: 2, borderRadius: 4, bgcolor: "#FFF8E1", textAlign: "center", border: "1px dashed #FFB74D" }}>
                <Typography variant="body2" gutterBottom color="text.secondary">This chat is currently unassigned.</Typography>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AssignmentIcon />}
                  onClick={() => handleAssignChat(selectedContact)}
                  sx={{
                    bgcolor: "#FF9800",
                    borderRadius: 20,
                    textTransform: "none",
                    boxShadow: "none",
                    '&:hover': { bgcolor: "#F57C00" }
                  }}
                >
                  Assign to me
                </Button>
              </Box>
            )}
          </>
        ) : (
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", opacity: 0.6 }}>
            <Box sx={{
              p: 4,
              borderRadius: "50%",
              bgcolor: "rgba(3, 169, 244, 0.05)",
              mb: 3
            }}>
              <ChatIcon sx={{ fontSize: 64, color: "#81D4FA" }} />
            </Box>
            <Typography variant="h5" color="text.secondary" fontWeight="600">No Chat Selected</Typography>
            <Typography variant="body2" color="text.secondary">Select a conversation from the sidebar to start messaging</Typography>
          </Box>
        )}
      </StyledChatArea>
      {/* Global Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })} sx={{ borderRadius: 4, boxShadow: "0 8px 24px rgba(0,0,0,0.1)" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </DashboardContainer>
  );
}